import { useCallback, useMemo, useRef, useState } from 'react';
import { components } from 'react-select';

import { ReactSelectInstance, TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import TranslateLangsMap from '@/utils/translate-langs-map.json';
import TranslateLangsV2 from '@/utils/translate-langs-v2.json';
import toastTrigger from '@ui/toast/toast-trigger';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import EditStateSelect from '@ui/edit-state-wrapper/edit-state-select';
import LanguageSelectorOption from './language-selector-option';
import { assertIsCustomReactOption } from '@/utils/assertions/assert-is-custom-react-option';
import LanguageSelectorMultiValue from './language-selector-multi-value';
import handleLanguageMenuNavigating from './handle-language-menu-navigating';
import preventMultiValueArrowNav from '@ui/edit-state-wrapper/prevent-multi-value-arrow-nav';
import { EToastTypes } from '@ui/toast/toast.types';

const LANGUAGE_OPTIONS_V2 = TranslateLangsV2.map((lang) => {
  return {
    value: lang.code,
    label: lang.label,
    meta: { value: lang.code, label: lang.label },
  };
});

type TLanguagesSelectorProps = {
  defaultLanguage: string;
  onMenuClose?: (selectedLanguages: TReactSelectOption[]) => void;
  defaultSelectedLanguages?: TReactSelectOption[];
  nonV2Languages?: string[];
  onBaseSelect?: (data: TReactSelectOption) => void;
  dynamicMenuPlacement?: boolean;
  dynamicMenuPlacementOffset?: number;
  isMulti?: boolean;
  hideBaseSelector?: boolean;
  handleChange?: (options: TReactSelectOption[]) => void;
  recalculateViewableItems?: number;
};

const LanguagesSelector = ({
  defaultLanguage,
  defaultSelectedLanguages,
  onMenuClose,
  nonV2Languages,
  onBaseSelect,
  dynamicMenuPlacement,
  dynamicMenuPlacementOffset = 0,
  isMulti = true,
  hideBaseSelector,
  handleChange,
  recalculateViewableItems,
}: TLanguagesSelectorProps) => {
  const selectRef = useRef<ReactSelectInstance>(null);

  const [selectedLanguages, setSelectedLanguages] = useState<TReactSelectOption[]>(defaultSelectedLanguages || []);
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);

  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  // if nonV2Languages, then add them to the list in order to maintain backward compatibility for older projects
  const options: TReactSelectOption[] = useMemo(() => {
    return nonV2Languages?.length
      ? [
          ...LANGUAGE_OPTIONS_V2,
          ...nonV2Languages.map((lang) => {
            const match = (TranslateLangsMap as Record<string, string>)[lang];
            if (!match)
              return {
                // return empty so we can filter it out
                value: '',
                label: '',
                meta: { value: '', label: '' },
              };
            return {
              value: lang,
              label: match,
              meta: { value: lang, label: match },
            };
          }),
        ]
          .filter((option) => option.value !== '')
          .sort((a, b) => a.label.localeCompare(b.label))
      : LANGUAGE_OPTIONS_V2;
  }, [nonV2Languages]);

  const handleOptionChange = useCallback(
    (options: TReactSelectOption[]) => {
      if (!options?.length) return; // never want to end up in a situation without a language

      setSelectedLanguages(options);
      handleChange?.(options);
      // if we only have one language, or
      // if not multi, we always want to update the base when the language changes
      if (options.length === 1 || (!isMulti && onBaseSelect && options.length)) {
        onBaseSelect?.(options[0]);
      }
    },
    [handleChange, isMulti, onBaseSelect]
  );

  const handleMenuClose = useCallback(() => {
    onMenuClose?.(selectedLanguages);
  }, [onMenuClose, selectedLanguages]);

  const handleBaseSelect = useCallback(
    (selectedBase: TReactSelectOption) => {
      // select the option (if not selected)
      setSelectedLanguages((prev) => {
        if (!prev.find((lang) => lang.value === selectedBase.value)) {
          return [...prev, selectedBase];
        }
        return prev;
      });
      // and call the props select method (parent component will handle the default language becuase this selector is used in multiple places differently)
      onBaseSelect?.(selectedBase);
    },
    [onBaseSelect]
  );

  return (
    <EditStateSelect
      disabled={!hasPermissions}
      Label="LANGUAGES"
      placeholder="Select languages"
      ref={selectRef}
      options={options}
      value={selectedLanguages}
      dynamicMenuPlacement={dynamicMenuPlacement}
      dynamicMenuPlacementOffset={dynamicMenuPlacementOffset}
      onMenuClose={handleMenuClose}
      onChange={handleOptionChange}
      onFocusWithinChange={setIsFocusedWithin}
      cannotClearOptions={options.filter((option) => option.value === defaultLanguage)}
      onKeyDown={(e) => {
        preventMultiValueArrowNav(e);
        handleLanguageMenuNavigating(selectRef)(e);
      }}
      recalculateViewableItems={recalculateViewableItems}
      isMulti={isMulti}
      unfocusedMultiValueContainerProps={{ minHeight: '28px', height: '28px' }}
      focusedMultiValueContainerProps={{ minHeight: '28px', height: '28px' }}
      styles={{
        control: () => ({
          height: '36px',
        }),
        option: (base, props) => ({
          ...base,
          padding: '8px 16px',
          height: '32px',
          cursor: 'pointer',
          backgroundColor: 'var(--global-cont-2) !important',
          ...(props?.isFocused
            ? {
                backgroundColor: 'var(--global-cont-3) !important',
              }
            : {}),
        }),
      }}
      onCannotClearOptions={() => {
        toastTrigger('The base language cannot be removed', {
          description: 'Please select another base language first',
          type: EToastTypes.warning,
          closeOnClick: true,
          autoClose: 5000,
          toastId: 'base-lang-warning',
        });
      }}
      Components={{
        MultiValueLabel: (option) => {
          try {
            assertIsCustomReactOption(option.data);
          } catch (error) {
            console.error(error);
            return <></>;
          }
          const value = option.data?.value;
          const isBase = value === defaultLanguage;

          return (
            <components.MultiValueLabel {...option}>
              <LanguageSelectorMultiValue
                isBase={isBase}
                children={option.children}
                isFocusedWithin={isFocusedWithin}
                selectedSize={selectedLanguages.length}
              />
            </components.MultiValueLabel>
          );
        },
        Option: (option) => {
          const data = option.data;
          try {
            assertIsCustomReactOption(data);
          } catch (error) {
            console.error(error);
            return <></>;
          }
          return (
            <components.Option {...option}>
              <LanguageSelectorOption
                data={data}
                isFocused={option.isFocused}
                isSelected={option.isSelected}
                defaultLanguage={defaultLanguage}
                hideBaseSelector={hideBaseSelector}
                onBaseSelect={handleBaseSelect}
              />
            </components.Option>
          );
        },
      }}
    />
  );
};

export default LanguagesSelector;
